import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bell, LayoutGrid, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import {
  Button,
  EmptyState,
  FilterBar,
  PageHeader,
  ToggleGroup,
  pageLayoutClass,
} from "@/components/ui";
import {
  CountTabLabel,
  ListResultsCard,
  type CountTabTone,
} from "@/components/common";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import {
  adminNotificationService,
  type AdminNotificationCategory,
  type AdminNotificationSeverity,
  type AdminNotificationStatus,
} from "@/services/adminNotificationService";
import {
  adminNotificationKeys,
  useAdminNotificationStatusCounts,
} from "@/hooks/useAdminNotificationStatusCounts";
import { NotificationCard } from "./NotificationCard";
import { NotificationBulkBar } from "./NotificationBulkBar";
import { NotificationListPagination } from "./NotificationListPagination";
import { NOTIFICATIONS_PAGE_SIZE } from "./constants";
import { groupNotificationsByDay } from "./notificationGroups";
import {
  CATEGORY_OPTIONS,
  SEVERITY_OPTIONS,
  parseCategory,
  parsePage,
  parseSeverity,
  parseStatusTab,
  type StatusTab,
} from "./notificationFilters";
import { useNotificationAction } from "./useNotificationAction";
import {
  CATEGORY_BADGE,
  SEVERITY_CARD_ICON,
  SEVERITY_SIGNAL,
} from "./notificationBadgeConfig";

const STATUS_TABS: { tab: StatusTab; tone: CountTabTone }[] = [
  { tab: "open", tone: "error" },
  { tab: "acknowledged", tone: "accent" },
  { tab: "resolved", tone: "success" },
  { tab: "all", tone: "neutral" },
];

export default function AdminNotificationsPage() {
  const { t } = useTranslation("adminNotifications");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const statusTab = parseStatusTab(searchParams.get("status"));
  const severityFilter = parseSeverity(searchParams.get("severity"));
  const categoryFilter = parseCategory(searchParams.get("category"));
  const page = parsePage(searchParams.get("page"));

  const listStatus: AdminNotificationStatus | undefined =
    statusTab === "all" ? undefined : statusTab;

  const secondaryFilterParams = useMemo(
    () => ({
      severity: severityFilter || undefined,
      category: categoryFilter || undefined,
    }),
    [severityFilter, categoryFilter],
  );

  const listQueryKey = useMemo(
    () =>
      [
        ...adminNotificationKeys.all,
        "list",
        statusTab,
        severityFilter,
        categoryFilter,
        page,
      ] as const,
    [statusTab, severityFilter, categoryFilter, page],
  );

  const {
    data: listData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: listQueryKey,
    queryFn: () =>
      adminNotificationService.list({
        skip: (page - 1) * NOTIFICATIONS_PAGE_SIZE,
        limit: NOTIFICATIONS_PAGE_SIZE,
        status: listStatus,
        ...secondaryFilterParams,
      }),
    placeholderData: (previous) => previous,
  });

  const { data: statusCounts } = useAdminNotificationStatusCounts(
    secondaryFilterParams,
    true,
  );

  const items = listData?.items ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / NOTIFICATIONS_PAGE_SIZE));

  const allPageSelected =
    items.length > 0 && items.every((item) => selectedIds.has(item.id));
  const somePageSelected = items.some((item) => selectedIds.has(item.id));
  const canBulkAcknowledge = items.some(
    (item) => selectedIds.has(item.id) && item.status === "open",
  );
  const canBulkResolve = items.some(
    (item) => selectedIds.has(item.id) && item.status !== "resolved",
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const setPage = useCallback(
    (nextPage: number) => {
      const clamped = Math.min(Math.max(1, nextPage), totalPages);
      const newParams = new URLSearchParams(searchParams);
      if (clamped <= 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", String(clamped));
      }
      setSearchParams(newParams);
      clearSelection();
    },
    [searchParams, setSearchParams, totalPages, clearSelection],
  );

  useEffect(() => {
    if (listData && page > totalPages) {
      setPage(totalPages);
    }
  }, [listData, page, totalPages, setPage]);

  const updateFilter = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== "page") {
      newParams.delete("page");
    }
    setSearchParams(newParams);
    clearSelection();
  };

  const invalidateNotificationQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: adminNotificationKeys.all });
  }, [queryClient]);

  const { actionId, runAction } = useNotificationAction({
    listQueryKey,
    tab: statusTab,
    onUpdated: (id, staysInTab) => {
      setSelectedIds((prev) => {
        if (!prev.has(id)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (!staysInTab && items.length <= 1 && page > 1) {
        setPage(page - 1);
      }
    },
  });

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const item of items) {
          next.delete(item.id);
        }
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const item of items) {
        next.add(item.id);
      }
      return next;
    });
  };

  const toggleSelection = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const runBulkAction = async (action: "acknowledge" | "resolve") => {
    const targets = items.filter((item) => {
      if (!selectedIds.has(item.id)) {
        return false;
      }
      if (action === "acknowledge") {
        return item.status === "open";
      }
      return item.status !== "resolved";
    });
    if (targets.length === 0) {
      return;
    }

    setBulkBusy(true);
    const results = await Promise.allSettled(
      targets.map((item) =>
        action === "acknowledge"
          ? adminNotificationService.acknowledge(item.id)
          : adminNotificationService.resolve(item.id),
      ),
    );
    const succeeded = results.filter(
      (result) => result.status === "fulfilled",
    ).length;
    const failed = results.length - succeeded;

    invalidateNotificationQueries();
    clearSelection();
    setBulkBusy(false);

    if (succeeded > 0) {
      toast.success(
        t(
          action === "acknowledge"
            ? "bulk.acknowledgeSuccess"
            : "bulk.resolveSuccess",
          { count: succeeded },
        ),
      );
    }
    if (failed > 0) {
      toast.error(t("bulk.partialError", { count: failed }));
    }
  };

  const counts = statusCounts ?? {
    open: 0,
    acknowledged: 0,
    resolved: 0,
    all: 0,
  };
  const groupedItems = groupNotificationsByDay(items);
  const listBusy = isLoading || isFetching;

  return (
    <div className={pageLayoutClass}>
      <PageHeader
        title={t("title")}
        description={t("description")}
        actions={
          <Button
            variant="outline"
            isLoading={listBusy}
            onClick={() => void refetch()}
            leftIcon={<RefreshCw className="h-4 w-4" />}
          >
            {tCommon("refresh")}
          </Button>
        }
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="-mx-1 overflow-x-auto px-1 pb-0.5">
          <ToggleGroup<StatusTab>
            variant="segmented-neutral"
            value={statusTab}
            onChange={(tab) => updateFilter("status", tab)}
            ariaLabel={t("filters.status")}
            options={STATUS_TABS.map(({ tab, tone }) => ({
              value: tab,
              label: (
                <CountTabLabel
                  label={t(`statusTabs.${tab}`)}
                  count={statusCounts ? counts[tab] : undefined}
                  tone={tone}
                  active={statusTab === tab}
                />
              ),
            }))}
          />
        </div>
        <FilterBar>
          <AdminFilterMenu<AdminNotificationSeverity | null>
            icon={AlertTriangle}
            name={t("filters.severity")}
            value={severityFilter}
            defaultValue={null}
            onChange={(severity) => updateFilter("severity", severity)}
            options={[
              { value: null, label: t("filters.any") },
              ...SEVERITY_OPTIONS.map((severity) => ({
                value: severity,
                label: t(`severity.${severity}`),
                icon: SEVERITY_CARD_ICON[severity],
                iconBadgeClassName: SEVERITY_SIGNAL[severity].icon,
              })),
            ]}
          />
          <AdminFilterMenu<AdminNotificationCategory | null>
            icon={LayoutGrid}
            name={t("filters.category")}
            value={categoryFilter}
            defaultValue={null}
            onChange={(category) => updateFilter("category", category)}
            options={[
              { value: null, label: t("filters.any") },
              ...CATEGORY_OPTIONS.map((category) => ({
                value: category,
                label: t(`category.${category}`),
                icon: CATEGORY_BADGE[category].icon,
                iconBadgeClassName: CATEGORY_BADGE[category].className,
              })),
            ]}
          />
        </FilterBar>
      </div>

      <ListResultsCard
        hideHeader
        isLoading={isLoading}
        isEmpty={!isLoading && items.length === 0}
        emptyState={
          <EmptyState
            icon={Bell}
            title={t("empty.title")}
            description={
              statusTab === "open"
                ? t("empty.openOnly")
                : t("empty.description")
            }
          />
        }
        contentClassName="p-0"
        pagination={
          !isLoading && items.length > 0 && total > NOTIFICATIONS_PAGE_SIZE ? (
            <NotificationListPagination
              page={page}
              total={total}
              isLoading={listBusy}
              onPageChange={setPage}
            />
          ) : null
        }
      >
        <NotificationBulkBar
          title={t("paginationSingle", { count: total })}
          selectedCount={selectedIds.size}
          pageCount={items.length}
          allPageSelected={allPageSelected}
          somePageSelected={somePageSelected}
          canAcknowledge={canBulkAcknowledge}
          canResolve={canBulkResolve}
          busy={bulkBusy}
          onToggleSelectAll={toggleSelectAll}
          onClearSelection={clearSelection}
          onAcknowledge={() => void runBulkAction("acknowledge")}
          onResolve={() => void runBulkAction("resolve")}
        />
        <div>
          {groupedItems.map(({ group, items: groupItems }) => (
            <section key={group}>
              <h2 className="border-b border-surface-200/70 bg-surface-50 px-5 py-1.5 text-xs font-medium text-surface-500 dark:bg-surface-100/40">
                {t(`groups.${group}`)}
              </h2>
              <div className="divide-y divide-surface-200/70 border-b border-surface-200/70 last:border-b-0">
                {groupItems.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    isBusy={actionId === notification.id || bulkBusy}
                    selectable
                    selected={selectedIds.has(notification.id)}
                    onSelectedChange={(selected) =>
                      toggleSelection(notification.id, selected)
                    }
                    onAcknowledge={(id) => void runAction(id, "acknowledge")}
                    onResolve={(id) => void runAction(id, "resolve")}
                    onReopen={(id) => void runAction(id, "reopen")}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </ListResultsCard>
    </div>
  );
}
